/**
 * Platform-agnostic React hook exposing the current connectivity
 * snapshot maintained by `NetworkMonitor`. Both web and mobile
 * consume this; the platform-specific wiring (browser online/offline
 * events on web, NetInfo on mobile) lives in each app's
 * `NetworkInitializer` and feeds the same singleton monitor.
 *
 * The `wasOffline` flag is a one-shot — set when transitioning
 * offline → online, then cleared 1 s after a consumer reads it.
 * That window is enough for downstream effects (toast, queue
 * trigger) to react exactly once per recovery.
 */
import { useEffect, useState } from 'react';

import type { NetworkStatus } from '@splitfiesta/shared';

import {
  checkIsOnline,
  checkWasOffline,
  clearWasOffline,
  subscribeNetworkStatus,
} from '../network/NetworkMonitor';

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(checkIsOnline());
  const [wasOffline, setWasOffline] = useState(checkWasOffline());

  useEffect(() => {
    const unsubscribe = subscribeNetworkStatus(() => {
      setIsOnline(checkIsOnline());
      setWasOffline(checkWasOffline());
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (wasOffline && isOnline) {
      const timer = setTimeout(() => {
        clearWasOffline();
        setWasOffline(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [wasOffline, isOnline]);

  return { isOnline, wasOffline };
}
